<template>
  <div class="screen-time-chart-container">
    <div class="screen-time-chart" :class="chartStyles" ref="chartContainer"></div>

    <div class="screen-time-chart__tooltip" ref="tooltipTemplate" style="display: none;">
      <div class="screen-time-chart__tooltip-date" data-date></div>
      <div class="screen-time-chart__tooltip-week-day" data-week-day></div>
      <div class="screen-time-chart__tooltip-screen-time" data-screen-time></div>

      <div class="screen-time-chart__tooltip-pointer" data-pointer></div>
      <div class="screen-time-chart__tooltip-pointer-end" data-pointer-end></div>
    </div>

    <div class="screen-time-chart__time" ref="timeTemplate" style="display: none;">
      <span class="screen-time-chart__time-label" data-time-label></span>
      <span class="screen-time-chart__time-unit" data-time-unit></span>
    </div>

    <div class="screen-time-chart__date-label" ref="dateLabel"></div>
  </div>
</template>

<script>
  import variables from '@/assets/scss/_variables.scss'
  import Highcharts from 'highcharts'
  import { getTimeString } from '@/services/utils.js'
  import * as intl from '@/services/intl'
  import i18n from '@/services/i18n'

  // Load the exporting module.
  import Exporting from 'highcharts/modules/exporting'
  // Initialize exporting module.
  Exporting(Highcharts)

  const dayDurationInMiliseconds = 24 * 60 * 60 * 1000
  const dayDurationInSeconds = 24 * 60 * 60
  const chartStyles = {
    colors: {
      bars: variables.colorPrimary,
      gridAndLabels: '#f2f2f2',
    },

    sizes: {
      gridLines: 2
    }
  }

  export default {
    props: {
      data: {
        required: true,
        type: Array
      },

      startDate: {
        required: true,
        type: String
      },

      endDate: {
        required: true,
        type: String
      },

      chartPeriod: {
        required: true,
        type: String
      }
    },

    data() {
      const vueComponent = this

      return {
        chart: null,
        options: {
          chart: {
            type: 'column',
            spacingTop: 45,
            style: {
              margin: '0 auto'
            },
            scrollablePlotArea: {
              minWidth: 400,
              scrollPositionX: 1
            }
          },
          responsive: {
            rules: [{
              condition: {
                maxWidth: 400
              },
              chartOptions: {
                chart: {
                  spacingTop: 20,
                  height: 200
                },
                plotOptions: {
                  series: {
                    pointWidth: 5,
                    borderRadius: 0,
                    dataLabels: {
                      style: {
                        fontSize: '12px'
                      }
                    }
                  }
                },
                xAxis: {
                  labels: {
                    style: {
                      fontSize: '12px'
                    }
                  }
                }
              }
            }]
          },
          time: {
            useUTC: false
          },
          series: [{
            pointPlacement: 'between',
            states: {
              hover: {
                brightness: 0
              }
            }
          }],
          plotOptions: {
            series: {
              pointWidth: 20,
              color: variables.colorPrimary,
              borderRadius: 12,
              dataLabels: {
                formatter() {
                  return vueComponent.dataLabelFormatter(this.y)
                },
                allowOverlap: true,
                style: {
                  fontFamily: 'Sofia Pro',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#65636a'
                }
              }
            }
          },
          xAxis: {
            type: 'datetime',
            startOfWeek: 0,
            labels: {
              autoRotation: false,
              formatter() {
                return vueComponent.xAxisLabelFormatter(this)
              },
              style: {
                display: 'inline-block',
                fontFamily: 'Sofia Pro',
                fontWeight: 'bold',
                fontSize: '24px',
                color: 'rgba(85, 85, 85, .6)'
              },
              useHTML: true,
              align: 'left'
            },
            gridLineWidth: chartStyles.sizes.gridLines,
            gridLineColor: chartStyles.colors.gridAndLabels,
            minorGridLineWidth: chartStyles.sizes.gridLines,
            minorGridLineDashStyle: 'dash',
            minorGridLineColor: chartStyles.colors.gridAndLabels,
            tickColor: chartStyles.colors.gridAndLabels,
            tickWidth: chartStyles.sizes.gridLines,
            minorTickLength: 12,
            minorTickWidth: chartStyles.sizes.gridLines,
            minorTickColor: chartStyles.colors.gridAndLabels,
            lineWidth: 0
          },
          yAxis: [
            {
              min: 0,
              max: dayDurationInSeconds,
              tickInterval: 0.5 * dayDurationInSeconds,
              title: {
                text: null
              },
              labels: {
                align: 'center',
                formatter() {
                  return vueComponent.yAxisLabelFormatter(this)
                },
                useHTML: true
              },
              gridLineColor: chartStyles.colors.gridAndLabels,
              gridLineWidth: chartStyles.sizes.gridLines,
              lineColor: chartStyles.colors.gridAndLabels,
              lineWidth: chartStyles.sizes.gridLines
            },
            {
              linkedTo: 0,
              opposite: true,
              title: {
                text: null
              },
              labels: {
                align: 'center',
                formatter() {
                  return vueComponent.yAxisLabelFormatter(this)
                },
                useHTML: true
              },
              gridLineColor: chartStyles.colors.gridAndLabels,
              gridLineWidth: chartStyles.sizes.gridLines,
              lineColor: chartStyles.colors.gridAndLabels,
              lineWidth: chartStyles.sizes.gridLines
            }
          ],
          tooltip: {
            shape: 'square',
            positioner: vueComponent.tooltipPositioner,
            useHTML: true,
            formatter() {
              return vueComponent.tooltipFormatter(this)
            },
            padding: 0,
            borderWidth: 0,
            shadow: false,
            hideDelay: 150,
          },
          exporting: {
            enabled: false
          },
          credits: {
            enabled: false
          },
          title: {
            text: null
          },
          legend: {
            enabled: false
          }
        }
      }
    },

    mounted() {
      this.initChart()
    },

    methods: {
      initChart() {
        this.chart = Highcharts.chart(this.$refs.chartContainer, this.options)
        this.updateChart(this.data)
      },

      yAxisLabelFormatter(label) {
        let timeNode = this.$refs.timeTemplate.cloneNode(true)
        let timeLabelNode = timeNode.querySelector('[data-time-label]')
        let timeUnitNode = timeNode.querySelector('[data-time-unit]')

        timeNode.removeAttribute('style')

        timeLabelNode.innerHTML = label.value / (60 * 60)
        timeUnitNode.innerHTML = label.value ? 'h' : ''

        return timeNode.outerHTML
      },

      xAxisLabelFormatter(label) {
        if (this.chartPeriod === 'week') {
          let labelDate = new Date(label.value).getDate()
          let currentDate = new Date().getDate()

          let dateLabelNode = this.$refs.dateLabel.cloneNode(true)
          let dayOfWeek = intl.getDayNameFromTimestamp(label.value)
          dateLabelNode.textContent = dayOfWeek

          if (labelDate === currentDate) {
            dateLabelNode.classList.add('screen-time-chart__date-label_current')
          }

          return dateLabelNode.outerHTML
        }

        let weekNumberFromToDate = intl.dateDiffFromNow(label.value, 'week')

        return weekNumberFromToDate === 0 ? i18n.t('thisWeek') : intl.localDateShort(label.value)
      },

      tooltipPositioner(labelWidth, labelHeight, point) {
        let xAxisStart = this.chart.plotLeft
        let xAxisEnd = xAxisStart + this.chart.plotWidth
        let xPointPosition = xAxisStart + point.plotX
        let tooltipX = xPointPosition - labelWidth / 2

        if (tooltipX < xAxisStart) {
          tooltipX = xAxisStart
        }

        if (tooltipX + labelWidth > xAxisEnd) {
          tooltipX = xAxisEnd - labelWidth
        }

        let highChartsContainer = document.querySelector('.highcharts-container')
        let pointerNode = highChartsContainer.querySelector('[data-pointer]')
        let pointerEndNode = highChartsContainer.querySelector('[data-pointer-end]')
        let chartHeight = this.chart.plotSizeY

        pointerNode.style.height = chartHeight - (chartHeight - point.plotY) + 'px'
        pointerNode.style.left = xPointPosition - tooltipX + 'px'
        pointerEndNode.style.left = pointerNode.style.left
        pointerEndNode.style.bottom = -(this.chart.plotHeight + 3) + 'px'

        return { x: tooltipX, y: 0 }
      },

      tooltipFormatter(tooltip) {
        let tooltipNode = this.$refs.tooltipTemplate.cloneNode(true)
        let dateNode = tooltipNode.querySelector('[data-date]')
        let weekDayNode = tooltipNode.querySelector('[data-week-day]')
        let screenTimeNode = tooltipNode.querySelector('[data-screen-time]')

        tooltipNode.removeAttribute('style')

        dateNode.innerHTML = intl.localDateShort(tooltip.x)
        weekDayNode.innerHTML = intl.getDayNameFromTimestamp(tooltip.x)

        let screenTime = Math.floor(tooltip.y / 60)
        let screenTimeHours = Math.floor(screenTime / 60)
        let screenTimeMinutes = screenTime % 60

        screenTimeNode.innerHTML = `${getTimeString(screenTimeHours)}h ${getTimeString(screenTimeMinutes)}m`

        return tooltipNode.outerHTML
      },

      dataLabelFormatter(value) {
        if (!value) {
          return ''
        }

        let screenTime = Math.floor(value / 60)
        let screenTimeHours = Math.floor(screenTime / 60)
        let screenTimeMinutes = screenTime % 60
        let hoursString = `${screenTimeHours}h`

        return screenTimeMinutes ? `${hoursString}${getTimeString(screenTimeMinutes)}m` : hoursString
      },

      getDataForChart(screenTimeData) {
        let dataMap = {}

        screenTimeData.forEach(dataSet => {
          dataMap[dataSet.date] = dataSet.sum_in_seconds
        })

        let data = []

        for (let date = moment(this.startDate); +date <= +moment(this.endDate); date = date.add(1, 'day')) {
          data.push([+date, dataMap[date.format('YYYY-MM-DD')] || 0])
        }

        return data
      },

      updateChart(data) {
        this.chart.update({
          series: [{
            data: this.getDataForChart(data)
          }],
          xAxis: {
            min: +moment(this.startDate),
            max: +moment(this.endDate),
            tickInterval: this.chartPeriod === 'month' ? 7 * dayDurationInMiliseconds : dayDurationInMiliseconds,
            tickLength: this.chartPeriod === 'month' ? 42 : 0,
            minorTickInterval: this.chartPeriod === 'month' ? 'auto' : null,
            gridLineDashStyle: this.chartPeriod === 'week' ? 'dash' : 'solid',
            labels: {
              style: {
                paddingTop: this.chartPeriod === 'week' ? '6px' : '32px'
              }
            }
          },
          plotOptions: {
            series: {
              dataLabels: {
                enabled: this.chartPeriod === 'week'
              }
            }
          },
          tooltip: {
            enabled: this.chartPeriod === 'month'
          }
        })
      }
    },

    computed: {
      chartStyles() {
        return {
          'screen-time-chart-mobile': this.$mq === 'mobile'
        }
      },

      ...mapGetters('auth', {
        locale: 'locale'
      })
    },

    watch: {
      data(newData) {
        this.updateChart(newData)
      },

      locale() {
        this.initChart()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .screen-time-chart {
    height: 100%;

    &__time-label,
    &__time-unit {
      font-family: 'Sofia Pro';
      font-weight: bold;
      color: rgba(#555, .6);
    }

    &__time-label {
      font-size: 24px;
    }

    &__time-unit {
      font-size: 16px;
    }

    &__tooltip {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 44px;
      padding: 0 16px;
      font-size: 24px;
      color: #fff;
      line-height: 1;
      background-color: map-get($colors, primary);
      border-radius: 8px;
    }

    &__tooltip-pointer,
    &__tooltip-pointer-end {
      position: absolute;
      z-index: 1;
      width: 4px;
      background-color: map-get($colors, primary);
      transform: translateX(-50%) translateY(100%);
    }

    &__tooltip-pointer {
      bottom: 0;
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
    }

    &__tooltip-pointer-end {
      height: 13px;
      border-radius: 2px;
    }

    &__tooltip-date,
    &__tooltip-week-day {
      display: flex;
      align-items: center;

      &::after {
        content: '';
        display: block;
        width: 4px;
        height: 4px;
        margin: 0 9px;
        border-radius: 50%;
        background-color: #fff;
      }
    }

    &__date-label {
      transform: translateX(calc(35px - 50%));

      &_current {
        color: map-get($colors, primary);
      }
    }
  }

  .screen-time-chart-mobile {
    padding: 0;
    margin: 0;

    .screen-time-chart__time-label {
      font-size: 12px;
    }

    .screen-time-chart__time-unit {
      font-size: 12px;
    }

    .screen-time-chart__date-label {
      transform: none;
    }

    .screen-time-chart__tooltip-pointer,
    .screen-time-chart__tooltip-pointer-end {
      transform: none;
    }
  }
</style>
